"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";

import { api } from "@/lib/api-client";
import type { FormVisitAnswerInput } from "@repo/api/modules/os/lib/form-visit-details";

type FormVisitTrackerProps = {
  formKind: string;
  formId: string;
  campaignId?: string | null;
  creativeId?: string | null;
  visitKey?: string;
  stepId: string;
  steps: Array<{ id: string; title: string }>;
  definitionVersion?: string;
  answerEntries?: FormVisitAnswerInput[];
  onSubmitReady?: (recordSubmit: () => void) => void;
};

export function FormVisitTracker({
  formKind,
  formId,
  campaignId,
  creativeId,
  visitKey: explicitVisitKey,
  stepId,
  steps,
  definitionVersion,
  answerEntries,
  onSubmitReady,
}: FormVisitTrackerProps) {
  const { mutate: recordFormVisit } =
    api.os.intakeForms.recordFormVisit.useMutation();
  const visitStartedAtRef = useRef(Date.now());
  const visitKeyRef = useRef<string | null>(null);
  const stepsKey = JSON.stringify(steps);
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const answerEntriesRef = useRef(answerEntries);
  answerEntriesRef.current = answerEntries;
  const visitKey = useMemo(() => {
    if (explicitVisitKey) return explicitVisitKey;
    // Client components also render on the server for public ad landing pages.
    if (typeof window === "undefined") return null;
    const clickId = new URLSearchParams(window.location.search).get("fbclid");
    const storageKey = `venturu_form_visit:v2:${formKind}:${formId}:${campaignId ?? "direct"}:${creativeId ?? "none"}:${clickId ?? "browser"}`;
    try {
      const existing = sessionStorage.getItem(storageKey);
      if (existing) return existing;
      const generated = clickId
        ? `meta:${clickId}`
        : `browser:${crypto.randomUUID()}`;
      sessionStorage.setItem(storageKey, generated);
      return generated;
    } catch {
      return clickId ? `meta:${clickId}` : `browser:${crypto.randomUUID()}`;
    }
  }, [campaignId, creativeId, explicitVisitKey, formId, formKind]);

  const record = useCallback(
    (
      event: "start" | "step" | "hide" | "submit",
      currentStepId: string | null,
    ) => {
      if (!visitKey) return;
      const steps = stepsRef.current;
      const step = currentStepId
        ? steps.find((candidate) => candidate.id === currentStepId)
        : null;
      recordFormVisit({
        trackingVersion: 2,
        formKind,
        formId,
        campaignId,
        creativeId,
        dedupeKey: visitKey,
        event,
        stepTitle: step?.title ?? null,
        stepIndex: step ? Math.max(0, steps.indexOf(step)) : null,
        stepCount: steps.length,
        definitionVersion,
        answerEntries: answerEntriesRef.current,
        observedAt: Date.now(),
        durationSeconds: Math.round(
          (Date.now() - visitStartedAtRef.current) / 1000,
        ),
      });
    },
    [
      campaignId,
      creativeId,
      formId,
      formKind,
      recordFormVisit,
      stepsKey,
      definitionVersion,
      visitKey,
    ],
  );

  useEffect(() => {
    if (!visitKey) return;
    if (visitKeyRef.current !== visitKey) {
      visitKeyRef.current = visitKey;
      visitStartedAtRef.current = Date.now();
      record("start", null);
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") record("hide", stepId);
    };
    const onPageHide = () => record("hide", stepId);
    // A mobile tab may be killed without a final visibility event. Duration is
    // elapsed session time, not a claim that the person paid attention throughout.
    const heartbeat = window.setInterval(() => {
      if (document.visibilityState === "visible") record("step", stepId);
    }, 10_000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [record, stepId, visitKey]);

  useEffect(() => {
    record("step", stepId);
  }, [record, stepId]);

  // Capture a selected business answer even if the visitor never advances.
  const answerKey = JSON.stringify(answerEntries);
  useEffect(() => {
    if (!answerKey) return;
    const timer = window.setTimeout(() => record("step", stepId), 500);
    return () => window.clearTimeout(timer);
  }, [answerKey, record, stepId]);

  useEffect(() => {
    // Register an explicit callback: completion may unmount the questionnaire
    // before a submitted-state effect runs. Failed completions never call it.
    onSubmitReady?.(() => record("submit", stepId));
  }, [onSubmitReady, record, stepId]);

  return null;
}
